import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, Alert, Typography,
} from '@mui/material';
import userService from '../../services/userService';

interface Props {
  open: boolean;
  onClose: () => void;
  eventId: string;
  venueId: string;
  venueName?: string;
}

const CATEGORIES = [
  { value: 'table_not_ready', label: 'Table was not prepared' },
  { value: 'wrong_drink', label: 'Wrong drink served' },
  { value: 'staff_rude', label: 'Staff was rude/unhelpful' },
  { value: 'no_games_available', label: 'Board games missing' },
  { value: 'venue_closed', label: 'Venue was closed' },
  { value: 'other', label: 'Other' },
];

export default function ReportHostModal({ open, onClose, eventId, venueId, venueName }: Props) {
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!category || !description) { setError('Please fill all fields'); return; }
    setLoading(true); setError('');
    try {
      await userService.reportHost({ venue_id: venueId, event_id: eventId, category, description });
      setSuccess(true);
    } catch (e: any) { console.warn('[ReportHostModal] submit report failed', e); setError(e.message || 'Failed'); }
    finally { setLoading(false); }
  };

  if (success) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Report Submitted</DialogTitle>
        <DialogContent><Typography>We'll review this promptly. Thank you.</Typography></DialogContent>
        <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Report Venue Issue{venueName ? ` — ${venueName}` : ''}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
          <InputLabel>Issue Category *</InputLabel>
          <Select value={category} onChange={e => setCategory(e.target.value)} label="Issue Category *">
            {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField fullWidth multiline rows={4} label="Description *" value={description}
          onChange={e => setDescription(e.target.value)} placeholder="Describe the issue..."
          inputProps={{ maxLength: 2000 }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="warning" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Submitting...' : 'Submit Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
